import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/posthog';
import { SelectableCard } from '../components/onboarding/SelectableCard';
import { PantallaConCumbre } from '../components/onboarding/corto/PantallaConCumbre';
import { dialogoDelRubro, REACCION_AL_RUBRO } from '../lib/cuestionarioCorto/dialogo';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import { SETUP_CATEGORIES, SETUP_CATEGORIES_KEY } from '../components/onboarding/accountSetup';
import { crearEmpresaDesdeCuestionario, nombreDeMarcaDelCuestionario, MARCA_POR_DEFECTO } from '../lib/altaDesdeCuestionario';
import { usePermissions } from '../context/PermissionContext';

/**
 * Cuestionario corto, paso 2: rubro / categoría de la marca. Lo pregunta Cumbre
 * (ADR-2026-10-31-el-cuestionario-corto-con-el-diseno-del-de-marca); los rubros siguen en la
 * cuadrícula con foto de siempre.
 *
 * - **Una sola opción**: sin el paso del rol ya no hay multi-select
 *   (ADR-2026-10-31-onboarding-corto-para-la-prueba).
 * - La lista scrollea por detrás del botón "Siguiente", pegado abajo.
 * - "Siguiente" queda deshabilitado hasta elegir una categoría. Al continuar **crea la
 *   empresa** —lo hacía el paso del tipo de contenido, que salió del corto— y va al paso del
 *   producto, que ya necesita `id_company` y créditos para generar de verdad. Si el alta
 *   falla, el usuario se queda acá con el motivo a la vista y reintenta sin perder lo elegido.
 */
export const AccountSetupCategory = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [selected, setSelected] = useState<string[]>([]);
  const [creando, setCreando] = useState(false);
  const creandoRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [nombre] = useState(() => {
    const n = nombreDeMarcaDelCuestionario();
    return n === MARCA_POR_DEFECTO ? null : n;
  });
  const tramos = useMemo(() => dialogoDelRubro(nombre), [nombre]);

  // Necesita sesión (viene post-login). Si no hay, vuelve a la bienvenida.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    trackEvent('onboarding_corto_paso', { paso: 'rubro', accion: 'visto' });
    return () => {
      active = false;
    };
  }, [navigate]);

  const toggle = (id: string) => setSelected([id]);

  const handleNext = async () => {
    // El guard es un REF, no el estado: `setCreando(true)` no se ve hasta el próximo
    // render, así que dos toques rápidos entrarían los dos con `creando === false` y
    // dispararían dos altas. El ref se actualiza en el acto.
    // Ver ADR-2026-08-01-alta-de-empresa-sin-carrera.
    if (selected.length === 0 || creandoRef.current) return;
    creandoRef.current = true;
    try {
      localStorage.setItem(SETUP_CATEGORIES_KEY, JSON.stringify(selected));
    } catch {}

    setCreando(true);
    setError(null);
    const res = await crearEmpresaDesdeCuestionario();
    if (!res.ok) {
      // Sin empresa no se puede seguir: el usuario se queda en el paso 2 y reintenta.
      setError(res.error ?? 'No pudimos crear tu cuenta. Probá de nuevo.');
      setCreando(false);
      creandoRef.current = false; // habilita el reintento
      return;
    }
    trackEvent('onboarding_corto_paso', { paso: 'rubro', accion: 'completado' });
    // Los permisos se calculan a partir de la empresa: sin refrescar, el usuario
    // entraría al paso del producto con los permisos de "sin empresa".
    await refreshPermissions();
    navigate(ONBOARDING_V2.setupProducto);
  };

  return (
    <PantallaConCumbre
      tramos={tramos}
      reaccion={creando ? REACCION_AL_RUBRO : null}
      animo={creando ? 'piensa' : 'escucha'}
      barra={
        <>
          {error && (
            <p role="alert" className="om-barra__aviso text-[#991B1B] md:text-right">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={handleNext}
            disabled={selected.length === 0 || creando}
            className={`om-boton md:w-auto md:px-10 ${creando ? 'is-enviando om-esqueleto-azul' : ''}`}
          >
            {creando ? 'Preparando tu cuenta…' : 'Siguiente'}
          </button>
        </>
      }
    >
      {/* La cuadrícula de rubros con foto de siempre, dentro de la pantalla de Cumbre. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3" role="radiogroup" aria-label="Elegí el rubro de tu marca">
        {SETUP_CATEGORIES.map((c) => (
          <SelectableCard
            key={c.id}
            label={c.label}
            image={c.image}
            selected={selected.includes(c.id)}
            onClick={() => !creando && toggle(c.id)}
            role="radio"
          />
        ))}
      </div>
    </PantallaConCumbre>
  );
};

export default AccountSetupCategory;
